/**
 * Utility functions for the reference checker application
 */
import React, { ReactElement } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { SortConfig, DeviceInfo } from '../types';
import { UAParser } from 'ua-parser-js';
import { PASSWORD_VALIDATION } from './Constants';

/**
 * Format date to a readable string
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Validate email format
 * @param email - Email to validate
 * @returns True if email is valid
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * Validate password format
 * @param password - Password to validate
 * @returns True if password is valid
 */
export const validatePassword = (password: string): boolean => {
  const hasLetters = new RegExp(PASSWORD_VALIDATION.LETTERS_PATTERN).test(password);
  const hasDigits = new RegExp(PASSWORD_VALIDATION.DIGITS_PATTERN).test(password);
  const hasSymbols = new RegExp(`[${PASSWORD_VALIDATION.SYMBOLS.replace(/[\\^\-\]]/g, '\\$&')}]`).test(password);
  const hasMinLength = password.length >= PASSWORD_VALIDATION.MIN_LENGTH;
  
  return hasLetters && hasDigits && hasSymbols && hasMinLength;
};

/**
 * Validate Japanese phone number format
 * @param phone - Phone number to validate
 * @returns True if phone number is valid
 */
export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^0\d{1,4}-\d{1,4}-\d{3,4}$|^0\d{9,11}$/;
  return phoneRegex.test(phone);
};

/**
 * Check if text contains emoji characters
 * @param text - Text to check for emojis
 * @returns True if text contains emojis
 */
export const containsEmoji = (text: string): boolean => {
  const emojiRegex = /(\ud83c[\udf00-\udfff])|(\ud83d[\udc00-\ude4f\ude80-\udeff])|[\u2600-\u26FF\u2700-\u27BF]/g;
  return emojiRegex.test(text);
};

/**
 * Truncate text with ellipsis if it exceeds max length
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text
 */
export const truncateText = (text: string | undefined, maxLength: number = 50): string => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

/**
 * Generate pagination array with ellipsis for large page counts
 * @param currentPage - Current active page
 * @param totalPages - Total number of pages
 * @returns Array of page numbers and ellipsis indicators
 */
export const generatePaginationArray = (currentPage: number, totalPages: number): Array<number | string> => {
  const pageNumbers: Array<number | string> = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    pageNumbers.push(1);

    if (currentPage > 3) {
      pageNumbers.push('...');
    }

    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      endPage = 4;
    } else if (currentPage >= totalPages - 2) {
      startPage = totalPages - 3;
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (currentPage < totalPages - 2) {
      pageNumbers.push('...');
    }

    pageNumbers.push(totalPages);
  }

  return pageNumbers;
};

/**
 * Render pagination component
 * @param currentPage - Current active page
 * @param totalPages - Total number of pages
 * @param paginate - Function to handle page change
 * @returns Pagination component
 */
export const renderPagination = (
  currentPage: number, 
  totalPages: number, 
  paginate: (pageNumber: number) => void
): ReactElement => {
  const pageNumbers = generatePaginationArray(currentPage, totalPages);

  return (
    <div className="pagination-container">
      <button 
        className={`pagination-button ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={() => currentPage > 1 && paginate(currentPage - 1)}
        disabled={currentPage === 1}
      >
        &lt;
      </button>

      {pageNumbers.map((number, index) => {
        if (number === '...') {
          const isLeftEllipsis = index < pageNumbers.indexOf(currentPage) || 
                                (typeof pageNumbers[index + 1] === 'number' && currentPage < (pageNumbers[index + 1] as number) && index === 1);
          const targetPage = isLeftEllipsis ? currentPage - 1 : currentPage + 1;

          return (
            <button
              key={`ellipsis-${index}`}
              className="pagination-button pagination-ellipsis"
              onClick={() => paginate(targetPage)}
            >
              ...
            </button>
          );
        }

        if (index > 0 && pageNumbers[index - 1] === '...' && typeof number === 'number') {
          return (
            <button
              key={number}
              className={`pagination-button ${currentPage === number ? 'active' : ''}`}
              onClick={() => paginate(number)}
            >
              {number}
            </button>
          );
        }

        if (index > 0 && typeof pageNumbers[index - 1] === 'number' && 
            typeof number === 'number' && number - (pageNumbers[index - 1] as number) > 1) {
          return (
            <React.Fragment key={`gap-${index}`}>
              <button
                className="pagination-button pagination-ellipsis"
                onClick={() => paginate(currentPage + 1)}
              >
                ...
              </button>
              <button
                className={`pagination-button ${currentPage === number ? 'active' : ''}`}
                onClick={() => paginate(number)}
              >
                {number}
              </button>
            </React.Fragment>
          );
        }

        if (typeof number === 'number') {
          return (
            <button
              key={number}
              className={`pagination-button ${currentPage === number ? 'active' : ''}`}
              onClick={() => paginate(number)}
            >
              {number}
            </button>
          );
        }

        return null;
      })}

      <button
        className={`pagination-button ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        &gt;
      </button>
    </div>
  );
};

/**
 * Get sort icon based on sort configuration
 * @param columnName - Column name
 * @param sortConfig - Sort configuration with key and direction
 * @returns Sort icon
 */
export const getSortIcon = (columnName: string, sortConfig?: SortConfig): ReactElement => {
  if (!sortConfig || sortConfig.key !== columnName) {
    return <FontAwesomeIcon icon={faSort} className="ms-1" />;
  }
  return sortConfig.direction === 'asc' ? 
    <FontAwesomeIcon icon={faSortUp} className="ms-1" /> : 
    <FontAwesomeIcon icon={faSortDown} className="ms-1" />;
};

/**
 * This function has been removed as token generation is now handled by the backend
 * @deprecated Use backend API to generate tokens instead
 */

/**
 * Get detailed device information including OS name and version
 * @returns DeviceInfo object with detailed device information
 */
export const getDetailedDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const vendor = navigator.vendor;
  const language = navigator.language;

  let osName = '';
  let osVersion = '';

  if (userAgent.indexOf('Windows NT') !== -1) {
    osName = 'Windows';

    if (userAgent.indexOf('Windows NT 10.0') !== -1) {
      osName = 'Windows 10';

      if (userAgent.indexOf('Windows NT 10.0; Win64') !== -1) {
        osVersion = 'バージョン 21H2';
      }
    } else if (userAgent.indexOf('Windows NT 6.3') !== -1) {
      osName = 'Windows 8.1';
    } else if (userAgent.indexOf('Windows NT 6.2') !== -1) {
      osName = 'Windows 8';
    } else if (userAgent.indexOf('Windows NT 6.1') !== -1) {
      osName = 'Windows 7';
    } else if (userAgent.indexOf('Windows NT 6.0') !== -1) {
      osName = 'Windows Vista';
    } else if (userAgent.indexOf('Windows NT 5.1') !== -1) {
      osName = 'Windows XP';
    }
  } 
  else if (userAgent.indexOf('Macintosh') !== -1) {
    osName = 'macOS';

    const macOSVersionMatch = userAgent.match(/Mac OS X ([0-9_]+)/) || 
                             userAgent.match(/Mac OS X ([0-9.]+)/);

    if (macOSVersionMatch) {
      const versionString = macOSVersionMatch[1].replace(/_/g, '.');
      const majorVersion = parseInt(versionString.split('.')[0]);
      const minorVersion = parseInt(versionString.split('.')[1]);

      if (majorVersion === 10) {
        if (minorVersion === 15) {
          osName = 'macOS Catalina';
          osVersion = 'バージョン 10.15';
        } else if (minorVersion === 14) {
          osName = 'macOS Mojave';
          osVersion = 'バージョン 10.14';
        } else if (minorVersion === 13) {
          osName = 'macOS High Sierra';
          osVersion = 'バージョン 10.13';
        } else if (minorVersion === 12) {
          osName = 'macOS Sierra';
          osVersion = 'バージョン 10.12';
        }
      } else if (majorVersion === 11) {
        osName = 'macOS Big Sur';
        osVersion = 'バージョン 11';
      } else if (majorVersion === 12) {
        osName = 'macOS Monterey';
        osVersion = 'バージョン 12';
      } else if (majorVersion === 13) {
        osName = 'macOS Ventura';
        osVersion = 'バージョン 13';
      } else if (majorVersion === 14) {
        osName = 'macOS Sonoma';
        osVersion = 'バージョン 14';
      }
    }
  } 
  else if (userAgent.indexOf('iPhone') !== -1 || userAgent.indexOf('iPad') !== -1 || userAgent.indexOf('iPod') !== -1) {
    if (userAgent.indexOf('iPhone') !== -1) {
      osName = 'iOS (iPhone)';
    } else if (userAgent.indexOf('iPad') !== -1) {
      osName = 'iOS (iPad)';
    } else if (userAgent.indexOf('iPod') !== -1) {
      osName = 'iOS (iPod)';
    }

    const iosVersionMatch = userAgent.match(/OS ([0-9_]+) like Mac OS X/);
    if (iosVersionMatch) {
      osVersion = 'バージョン ' + iosVersionMatch[1].replace(/_/g, '.');
    }
  } 
  else if (userAgent.indexOf('Android') !== -1) {
    osName = 'Android';

    const androidVersionMatch = userAgent.match(/Android ([0-9.]+)/);
    if (androidVersionMatch) {
      osVersion = 'バージョン ' + androidVersionMatch[1];
    }
  } 
  else if (userAgent.indexOf('Linux') !== -1) {
    osName = 'Linux';

    if (userAgent.indexOf('Ubuntu') !== -1) {
      osName = 'Ubuntu Linux';
    } else if (userAgent.indexOf('Fedora') !== -1) {
      osName = 'Fedora Linux';
    } else if (userAgent.indexOf('Debian') !== -1) {
      osName = 'Debian Linux';
    }
  }

  let browserName = '';
  let browserVersion = '';

  if (userAgent.indexOf('Firefox') !== -1) {
    browserName = 'Firefox';
    const versionMatch = userAgent.match(/Firefox\/([0-9.]+)/);
    if (versionMatch) {
      browserVersion = versionMatch[1];
    }
  } else if (userAgent.indexOf('Chrome') !== -1 && userAgent.indexOf('Edg') === -1 && userAgent.indexOf('OPR') === -1) {
    browserName = 'Chrome';
    const versionMatch = userAgent.match(/Chrome\/([0-9.]+)/);
    if (versionMatch) {
      browserVersion = versionMatch[1];
    }
  } else if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) {
    browserName = 'Safari';
    const versionMatch = userAgent.match(/Version\/([0-9.]+)/);
    if (versionMatch) {
      browserVersion = versionMatch[1];
    }
  } else if (userAgent.indexOf('Edg') !== -1) {
    browserName = 'Microsoft Edge';
    const versionMatch = userAgent.match(/Edg\/([0-9.]+)/);
    if (versionMatch) {
      browserVersion = versionMatch[1];
    }
  } else if (userAgent.indexOf('OPR') !== -1 || userAgent.indexOf('Opera') !== -1) {
    browserName = 'Opera';
    const versionMatch = userAgent.match(/OPR\/([0-9.]+)/) || userAgent.match(/Opera\/([0-9.]+)/);
    if (versionMatch) {
      browserVersion = versionMatch[1];
    }
  } else if (userAgent.indexOf('Trident') !== -1) {
    browserName = 'Internet Explorer';
    const versionMatch = userAgent.match(/rv:([0-9.]+)/) || userAgent.match(/MSIE ([0-9.]+)/);
    if (versionMatch) {
      browserVersion = versionMatch[1];
    }
  }

  return {
    platform,
    userAgent,
    vendor,
    language,
    osName,
    osVersion,
    browserName,
    browserVersion
  };
};

/**
 * Get simplified OS information for database storage
 * @returns Simple string with OS name and version
 */
export const getClientOSInfo = (): string => {
  const parser = new UAParser();
  const result = parser.getResult();
  return `${result.os.name} ${result.os.version}`;
/*
  const deviceInfo = getDetailedDeviceInfo();
  return `${deviceInfo.osName} ${deviceInfo.osVersion}`.trim();
*/
};

export const getClientBrowserInfo = (): string => {
  const parser = new UAParser();
  const result = parser.getResult();
  return `${result.browser.name} ${result.browser.version}`;
/*
  const browserInfo = getDetailedDeviceInfo();
  return `${browserInfo.browserName} ${browserInfo.browserVersion}`.trim();
*/
};

export const getClientIPAddress = (): string => {
  return '';
};

export const formatSurveyResponse = (value: any, inputType: 'radio' | 'textarea'): string => {
  if (inputType === 'textarea') {
    return value || '未回答';
  }
  else {
    if (value === '1' || value === 1) {
      return 'はい';
    } else if (value === '0' || value === 0) {
      return 'いいえ';
    }
  }
  return '未回答';
};
